import { useState } from 'react';
import type { CourbeAtelier } from '../../lib/training.ts';
import { MODES } from '../../lib/modes.ts';

/* Un atelier, une vignette.
 *
 * ── Pourquoi des petits multiples et non une courbe à cinq couleurs ─────────
 *
 * Cinq séries sur un même cadre demandent de tenir cinq couleurs en tête et de
 * démêler des lignes qui se croisent ; à cinq teintes, deux d'entre elles
 * finissent toujours trop proches pour qui ne les distingue pas bien — et on
 * lit cet écran en plein soleil. Les mêmes données rangées en vignettes se
 * lisent d'un coup d'œil : la forme de chaque courbe se compare à celle d'à
 * côté sans qu'aucune couleur n'ait à porter d'identité. C'est le nom écrit
 * au-dessus qui dit de quel atelier il s'agit.
 *
 * ── Pourquoi la même échelle partout ────────────────────────────────────────
 *
 * Zéro à cent pour toutes, et le trait de cinquante au même endroit. Une
 * échelle ajustée à chaque vignette rendrait chacune plus lisible et le tableau
 * entier mensonger : un atelier qui oscille entre 18 et 22 % aurait la même
 * allure que celui qui va de 20 à 80. Ici, la hauteur veut dire la même chose
 * d'une vignette à l'autre — c'est tout l'intérêt d'en mettre plusieurs.
 *
 * ── Ce que cet écran répond, et ce qu'il ne répond pas ──────────────────────
 *
 * « OÙ est-ce que je progresse ». Le « est-ce que je progresse » se lit sur
 * l'indice à composition constante, juste au-dessus : là, tous les ateliers
 * sont ramenés à une seule courbe. Les deux ne font pas double emploi, ils
 * répondent à deux questions dont l'une appelle l'autre.
 */

const W = 150;
const H = 64;
const PAD = 6;

const shortDate = (day: string): string => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y!, m! - 1, d!).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

/** Le nom de l'atelier : celui de l'exercice, ou le libellé du mode. */
const nomDe = (a: CourbeAtelier): string =>
    a.exerciseName ?? MODES.find((m) => m.mode === a.mode)?.label ?? 'Exercice';

function Vignette({ atelier }: { atelier: CourbeAtelier }) {
    const [hover, setHover] = useState<number | null>(null);
    const { points } = atelier;
    const x = (i: number) => PAD + (i / (points.length - 1)) * (W - 2 * PAD);
    const y = (v: number) => PAD + (1 - v / 100) * (H - 2 * PAD);
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.rate)}`).join(' ');
    const dernier = points[points.length - 1]!;
    const vu = hover === null ? null : points[hover]!;

    return (
        <div className="rounded-field bg-base-200 p-2">
            <div className="flex items-baseline justify-between gap-1">
                <span className="truncate text-xs font-bold">{nomDe(atelier)}</span>
                {/* « dernier » en toutes lettres : le chiffre est celui de la
                    DERNIÈRE sortie, quand la ligne du dessous donne le taux
                    d'ensemble. Deux pourcentages sans étiquette dans la même
                    vignette, et on ne sait plus lequel répond à quoi. */}
                <span className="shrink-0 whitespace-nowrap">
                    <span className="mr-1 text-[9px] uppercase tracking-wide opacity-50">
                        dernier
                    </span>
                    <span className="font-score text-sm tabular-nums">
                        {dernier.rate.toFixed(0)}%
                    </span>
                </span>
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} className="mt-1 h-auto w-full"
                role="img"
                aria-label={`${nomDe(atelier)} : ${points.map((p) => `${shortDate(p.day)} ${p.rate.toFixed(0)} %`).join(', ')}`}>
                {/* Le trait de cinquante, commun à toutes les vignettes : c'est
                    lui qui rend deux formes comparables d'un coup d'œil. */}
                <line x1={PAD} x2={W - PAD} y1={y(50)} y2={y(50)}
                    stroke="var(--chart-grid)" strokeWidth={1} />
                <path d={d} fill="none" stroke="var(--chart-current)" strokeWidth={2}
                    strokeLinejoin="round" strokeLinecap="round" />
                {points.map((p, i) => (
                    <g key={p.day}
                        onMouseEnter={() => setHover(i)}
                        onMouseLeave={() => setHover(null)}>
                        <rect x={x(i) - (W - 2 * PAD) / (points.length * 2)} y={0}
                            width={(W - 2 * PAD) / points.length} height={H} fill="transparent" />
                        <circle cx={x(i)} cy={y(p.rate)} r={i === points.length - 1 || hover === i ? 3.5 : 2.5}
                            fill="var(--chart-current)" />
                    </g>
                ))}
            </svg>

            {/* Le volume sous la vignette : quatre balles et quarante ne se
                lisent pas de la même façon, et la courbe ne le dit pas. */}
            <p className="mt-0.5 truncate text-[10px] opacity-60">
                {vu
                    ? `${shortDate(vu.day)} · ${vu.rate.toFixed(0)} % sur ${vu.shots} balles`
                    : `${atelier.balls} balles · ${atelier.rate.toFixed(0)} % d’ensemble`}
            </p>
        </div>
    );
}

export function AteliersMultiples({ ateliers }: { ateliers: CourbeAtelier[] }) {
    const [table, setTable] = useState(false);
    if (ateliers.length === 0) return null;

    return (
        <div>
            <p className="mb-2 text-xs opacity-60">
                Un atelier par vignette : c’est ici qu’on voit OÙ l’on progresse.
            </p>

            <div className="grid grid-cols-2 gap-2">
                {ateliers.map((a) => <Vignette key={a.cle} atelier={a} />)}
            </div>

            <button type="button" className="btn btn-ghost btn-xs mt-1"
                onClick={() => setTable((t) => !t)} aria-expanded={table}>
                {table ? 'Masquer' : 'Voir'} le tableau
            </button>

            {table && (
                <div className="mt-2 overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Atelier</th>
                                <th>Jour</th>
                                <th className="text-right">Balles</th>
                                <th className="text-right">Réussite</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ateliers.flatMap((a) => [...a.points].reverse().map((p) => (
                                <tr key={`${a.cle}-${p.day}`}>
                                    <td className="whitespace-nowrap">{nomDe(a)}</td>
                                    <td className="whitespace-nowrap">{shortDate(p.day)}</td>
                                    <td className="text-right tabular-nums opacity-70">{p.shots}</td>
                                    <td className="text-right tabular-nums">{p.rate.toFixed(0)}%</td>
                                </tr>
                            )))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
